import { msg } from 'gt-react';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Route as pathlessAuthenticatedRoute, requireAdmin } from '../../route';

const MaintenanceLayoutPage = lazyRouteComponent(
  () => import('./MaintenanceLayoutPage'),
  'MaintenanceLayoutPage',
);
const BackfillsPage = lazyRouteComponent(
  () => import('./backfills/BackfillsPage'),
  'BackfillsPage',
);
const DeferredIndexesPage = lazyRouteComponent(
  () => import('./indexes/DeferredIndexesPage'),
  'DeferredIndexesPage',
);
const SchemaReconciliationPage = lazyRouteComponent(
  () => import('./reconciliation/SchemaReconciliationPage'),
  'SchemaReconciliationPage',
);
const LargeTablesPage = lazyRouteComponent(
  () => import('./large-tables/LargeTablesPage'),
  'LargeTablesPage',
);

/**
 * Admin → Maintenance: operator tooling that changes the database outside a
 * deploy. Each tool is a tab; the layout owns the page header and tab bar.
 *
 * The tabs are declared here rather than in their own modules because a child
 * route in a separate file cannot state its parent's path segments, which
 * `custom/tanstack-route-file-path` checks the file location against.
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/admin/maintenance',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: MaintenanceLayoutPage,
  loader: () => ({
    crumb: msg('Maintenance', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

/** Bare `/admin/maintenance` opens the first tab. */
export const MaintenanceIndexRoute = createRoute({
  getParentRoute: () => Route,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/maintenance/backfills' });
  },
});

export const MaintenanceBackfillsRoute = createRoute({
  getParentRoute: () => Route,
  path: 'backfills',
  component: BackfillsPage,
  // `label` selects a backfill label path (or the unlabeled bucket) so the
  // filtered view is linkable.
  validateSearch: z.object({
    label: z.string().optional(),
  }),
  loader: () => ({
    crumb: msg('Backfills', { context: 'page breadcrumb' }),
  }),
});

export const MaintenanceIndexesRoute = createRoute({
  getParentRoute: () => Route,
  path: 'indexes',
  component: DeferredIndexesPage,
  loader: () => ({
    crumb: msg('Deferred indexes', { context: 'page breadcrumb' }),
  }),
});

export const MaintenanceLargeTablesRoute = createRoute({
  getParentRoute: () => Route,
  path: 'large-tables',
  component: LargeTablesPage,
  loader: () => ({
    crumb: msg('Large tables', { context: 'page breadcrumb' }),
  }),
});

export const MaintenanceReconciliationRoute = createRoute({
  getParentRoute: () => Route,
  path: 'reconciliation',
  component: SchemaReconciliationPage,
  loader: () => ({
    crumb: msg('Reconciliation', { context: 'page breadcrumb' }),
  }),
});

/**
 * The tab was called Schema drift before it grew the other direction. Bookmarks
 * and pasted links outlive a rename, so the old path still resolves.
 */
export const MaintenanceSchemaDriftRedirectRoute = createRoute({
  getParentRoute: () => Route,
  path: 'schema-drift',
  beforeLoad: () => {
    throw redirect({ to: '/admin/maintenance/reconciliation' });
  },
});

export const MaintenanceWorkerScalingRoute = createRoute({
  getParentRoute: () => Route,
  path: 'worker-scaling',
  component: lazyRouteComponent(
    () => import('./worker-scaling/WorkerScalingPage'),
    'WorkerScalingPage',
  ),
  loader: () => ({
    crumb: msg('Worker scaling', { context: 'page breadcrumb' }),
  }),
});
